import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { waLink } from "@/lib/whatsapp";
import { scrollToHash } from "@/components/Header";

const SLIDES = [
    "https://images.unsplash.com/photo-1645025020712-48c0ad4759c3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920",
    "https://images.unsplash.com/photo-1765224747184-05d35f23859a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920",
    "https://images.unsplash.com/photo-1748260526938-4fa5472eb796?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920",
];

const MaskedLine = ({ children, index }) => (
    <span className="block overflow-hidden pb-2">
        <motion.span
            className="block"
            custom={index}
            initial={{ y: "115%" }}
            animate={{ y: "0%" }}
            transition={{ delay: 0.5 + index * 0.16, duration: 1, ease: [0.22, 1, 0.36, 1] }}
        >
            {children}
        </motion.span>
    </span>
);

const Hero = () => {
    const [slide, setSlide] = useState(0);
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
    const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "60%"]);
    const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

    useEffect(() => {
        const t = setInterval(() => setSlide((s) => (s + 1) % SLIDES.length), 6000);
        return () => clearInterval(t);
    }, []);

    return (
        <section id="inicio" ref={ref} data-testid="hero-section" className="relative h-[100svh] overflow-hidden bg-white">
            <motion.div className="absolute inset-0" style={{ y: bgY }}>
                <AnimatePresence mode="sync">
                    <motion.img
                        key={slide}
                        src={SLIDES[slide]}
                        alt="LC Production — experiência musical em evento"
                        initial={{ opacity: 0, scale: 1.15 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{
                            opacity: { duration: 1.8, ease: "easeInOut" },
                            scale: { duration: 7, ease: "linear" },
                        }}
                        className="absolute inset-0 w-full h-full object-cover"
                    />
                </AnimatePresence>
            </motion.div>
            <div className="absolute inset-0 bg-gradient-to-t from-white via-white/40 to-white/50" />

            <motion.div
                style={{ y: contentY, opacity: contentOpacity }}
                className="relative z-10 h-full flex flex-col justify-end px-6 md:px-12 pt-28 pb-28 md:pb-24"
            >
                <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.3, duration: 1 }}
                    className="text-[10px] md:text-xs tracking-[0.45em] uppercase text-[#6A2022] mb-6"
                    data-testid="hero-eyebrow"
                >
                    LC Production — Rio de Janeiro
                </motion.p>

                <h1 className="text-4xl sm:text-5xl lg:text-7xl font-extrabold uppercase leading-[1.18] sm:leading-[1.14] lg:leading-[1.1] tracking-tight max-w-5xl" data-testid="hero-headline">
                    <MaskedLine index={0}>Não entregamos</MaskedLine>
                    <MaskedLine index={1}>apenas música.</MaskedLine>
                    <MaskedLine index={2}>
                        Criamos{" "}
                        <span className="font-editorial italic font-light normal-case text-[#6A2022] inline-block pb-1">
                            experiências.
                        </span>
                    </MaskedLine>
                </h1>

                <motion.p
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1.15, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                    className="mt-6 max-w-xl text-sm md:text-lg text-[#0A0A0A]/65 leading-relaxed"
                    data-testid="hero-subheadline"
                >
                    Da emoção da cerimônia à energia da pista, uma produtora para transformar cada
                    momento do seu evento.
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1.35, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                    className="mt-10 flex flex-col sm:flex-row gap-4"
                >
                    <button
                        onClick={() => scrollToHash("#experiencias")}
                        data-testid="hero-cta-experiencias"
                        className="inline-flex items-center justify-center gap-3 bg-[#0A0A0A] text-white px-8 py-4 text-[11px] tracking-[0.3em] uppercase font-semibold transition-colors duration-300 hover:bg-[#6A2022]"
                    >
                        Quero conhecer as experiências
                        <ArrowDown className="w-4 h-4" />
                    </button>
                    <a
                        href={waLink("geral")}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="hero-cta-whatsapp"
                        className="inline-flex items-center justify-center gap-3 border border-black/40 text-[#0A0A0A] px-8 py-4 text-[11px] tracking-[0.3em] uppercase transition-colors duration-300 hover:bg-[#6A2022] hover:border-[#6A2022] hover:text-white"
                    >
                        Solicitar orçamento
                        <ArrowUpRight className="w-4 h-4" />
                    </a>
                </motion.div>
            </motion.div>

            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 2, duration: 1 }}
                className="absolute bottom-8 right-6 md:right-12 z-10 hidden md:flex flex-col items-center gap-3"
                data-testid="hero-scroll-indicator"
            >
                <span className="text-[9px] tracking-[0.4em] uppercase text-[#0A0A0A]/50 [writing-mode:vertical-rl]">
                    Scroll to discover
                </span>
                <span className="w-px h-16 bg-black/30 scroll-line-anim" />
            </motion.div>
        </section>
    );
};

export default Hero;
